import { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  Plus, Search, Users, Calendar, Receipt, TrendingUp,
  FileDown, Sheet, Filter, X,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { clientsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency } from "@/lib/data";
import { byNewest } from "@/lib/utils";
import { Client } from "./types";
import ClientForm from "./components/ClientForm";
import ClientsTable, { SortCol, SortDir } from "./components/ClientsTable";
import ClientLedgerModal from "./components/ClientLedgerModal";
import PrintableClientsList from "./components/PrintableClientsList";

// ── Types ────────────────────────────────────────────────────────────────────
type BalanceFilter = "all" | "outstanding" | "cleared";
type EventsFilter = "all" | "with_events" | "no_events";

// ── Helpers ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, sub, color = "amber" }: {
  label: string; value: string; icon: any; sub?: string;
  color?: "amber" | "violet" | "emerald" | "rose";
}) {
  const colors: Record<string, string> = {
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-3 sm:p-4 flex items-center gap-3 sm:gap-4 min-w-0">
      <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-stone-400 font-medium mb-0.5 leading-tight">{label}</div>
        <div className="text-lg sm:text-xl font-bold text-stone-800 truncate">{value}</div>
        {sub && <div className="text-xs text-stone-400 mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
        active
          ? "bg-stone-800 text-white border-stone-800"
          : "bg-white text-stone-500 border-stone-200 hover:border-stone-400 hover:text-stone-700"
      }`}
    >
      {label}
    </button>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ClientsPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const { settings } = useSettings();
  const isStaff = user?.role === "staff";

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [balanceFilter, setBalanceFilter] = useState<BalanceFilter>("all");
  const [eventsFilter, setEventsFilter] = useState<EventsFilter>("all");

  // Sorting
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Modals
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [ledgerClient, setLedgerClient] = useState<Client | null>(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);

  // PDF print state
  const [printData, setPrintData] = useState<{
    clients: Client[];
    filterLabel: string;
    generatedAt: string;
    stats: { total: number; withEvents: number; totalRevenue: number; totalBalance: number };
  } | null>(null);

  // ── Data ─────────────────────────────────────────────────────────────────
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.list();
      setClients(response.data?.data || response.data || []);
    } catch {
      showToast("Failed to load clients", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Filter + Sort ─────────────────────────────────────────────────────────
  const filteredClients = useMemo(() => {
    let list = [...clients];

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.company_name?.toLowerCase().includes(q)
      );
    }

    // Balance filter
    if (balanceFilter === "outstanding") list = list.filter(c => (Number(c.balance) || 0) > 0);
    if (balanceFilter === "cleared") list = list.filter(c => (Number(c.balance) || 0) <= 0);

    // Events filter
    if (eventsFilter === "with_events") list = list.filter(c => (c.events_count || 0) > 0);
    if (eventsFilter === "no_events") list = list.filter(c => (c.events_count || 0) === 0);

    // Sort
    if (sortCol) {
      list.sort((a, b) => {
        let cmp = 0;
        if (sortCol === "name") cmp = a.name.localeCompare(b.name);
        else if (sortCol === "company") cmp = (a.company_name || "").localeCompare(b.company_name || "");
        else if (sortCol === "events") cmp = (Number(a.events_count) || 0) - (Number(b.events_count) || 0);
        else if (sortCol === "revenue") cmp = (Number(a.total_revenue) || 0) - (Number(b.total_revenue) || 0);
        else if (sortCol === "balance") cmp = (Number(a.balance) || 0) - (Number(b.balance) || 0);
        return sortDir === "asc" ? cmp : -cmp;
      });
    } else {
      // No column chosen: newest-created clients first.
      list.sort(byNewest);
    }

    return list;
  }, [clients, searchQuery, balanceFilter, eventsFilter, sortCol, sortDir]);

  const isFiltered = !!(searchQuery.trim() || balanceFilter !== "all" || eventsFilter !== "all");

  const stats = useMemo(() => ({
    total: clients.length,
    withEvents: clients.filter(c => (c.events_count ?? 0) > 0).length,
    totalRevenue: clients.reduce((sum, c) => sum + (Number(c.total_revenue) || 0), 0),
    totalBalance: clients.reduce((sum, c) => sum + (Number(c.balance) || 0), 0),
  }), [clients]);

  const filteredStats = useMemo(() => ({
    total: filteredClients.length,
    withEvents: filteredClients.filter(c => (c.events_count ?? 0) > 0).length,
    totalRevenue: filteredClients.reduce((sum, c) => sum + (Number(c.total_revenue) || 0), 0),
    totalBalance: filteredClients.reduce((sum, c) => sum + (Number(c.balance) || 0), 0),
  }), [filteredClients]);

  // ── Sort handler ──────────────────────────────────────────────────────────
  const handleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setBalanceFilter("all");
    setEventsFilter("all");
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const openAdd = () => { setEditingClient(null); setIsFormOpen(true); };
  const openEdit = (c: Client) => { setEditingClient(c); setIsFormOpen(true); };
  const closeForm = () => { setIsFormOpen(false); setEditingClient(null); };

  const openDelete = (c: Client) => {
    if ((c.events_count ?? 0) > 0) { showToast("Cannot delete a client with existing events", "error"); return; }
    setDeletingClient(c); setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingClient) return;
    setDeleteLoading(true);
    const prev = clients;
    setClients(c => c.filter(x => x.id !== deletingClient.id));
    try {
      await clientsApi.delete(deletingClient.id);
      showToast("Client deleted", "success");
      setDeleteModalOpen(false); setDeletingClient(null);
    } catch {
      setClients(prev);
      showToast("Failed to delete client", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: Partial<Client>) => {
    closeForm();
    try {
      if (editingClient) {
        const updated = { ...editingClient, ...data };
        setClients(c => c.map(x => x.id === editingClient.id ? updated : x));
        await clientsApi.update(editingClient.id, data);
        showToast("Client updated", "success");
      } else {
        const tempId = Date.now();
        // created_at keeps the optimistic row at the top under the newest-first default sort.
        const optimistic = { id: tempId, created_at: new Date().toISOString(), ...data, events_count: 0, total_revenue: 0, total_paid: 0, balance: 0 } as Client;
        setClients(c => [optimistic, ...c]);
        const response = await clientsApi.create(data);
        const created = response.data?.data || response.data;
        setClients(c => c.map(x => x.id === tempId ? created : x));
        showToast("Client added", "success");
      }
    } catch {
      showToast("Failed to save client", "error");
      loadData();
    }
  };

  // ── Exports ───────────────────────────────────────────────────────────────
  const buildFilterLabel = () => {
    const parts: string[] = [];
    if (searchQuery.trim()) parts.push(`Search: "${searchQuery}"`);
    if (balanceFilter !== "all") parts.push(balanceFilter === "outstanding" ? "Has Balance" : "Cleared");
    if (eventsFilter !== "all") parts.push(eventsFilter === "with_events" ? "With Events" : "No Events");
    return parts.length ? parts.join(" · ") : "All Clients";
  };

  const exportToExcel = () => {
    const rows = [
      ["#", "Client Name", "Company", "Phone", "Email", "Events", "Billed incl. GST (₹)", "Paid (₹)", "Balance (₹)"],
      ...filteredClients.map((c, i) => [
        i + 1,
        c.name,
        c.company_name || "",
        c.phone || "",
        c.email || "",
        Number(c.events_count) || 0,
        Number(c.total_revenue) || 0,
        Number(c.total_paid) || 0,
        Number(c.balance) || 0,
      ]),
      [
        "TOTAL", "", "", "", "",
        filteredClients.reduce((s, c) => s + (Number(c.events_count) || 0), 0),
        filteredClients.reduce((s, c) => s + (Number(c.total_revenue) || 0), 0),
        filteredClients.reduce((s, c) => s + (Number(c.total_paid) || 0), 0),
        filteredClients.reduce((s, c) => s + (Number(c.balance) || 0), 0),
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Column widths
    ws["!cols"] = [
      { wch: 5 }, { wch: 28 }, { wch: 22 }, { wch: 16 }, { wch: 28 },
      { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 16 },
    ];

    // Number format for currency columns (G, H, I = indices 5,6,7,8)
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let r = 1; r <= range.e.r; r++) {
      ["G", "H", "I"].forEach(col => {
        const ref = `${col}${r + 1}`;
        if (ws[ref] && typeof ws[ref].v === "number") ws[ref].z = "#,##0.00";
      });
    }

    // Freeze top row
    ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2", sqref: "A2" }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");

    // Metadata sheet
    const meta = XLSX.utils.aoa_to_sheet([
      ["Report", "Clients List"],
      ["Filter", buildFilterLabel()],
      ["Generated", new Date().toLocaleString("en-IN")],
      ["Total Records", filteredClients.length],
    ]);
    XLSX.utils.book_append_sheet(wb, meta, "Info");

    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `clients_${date}.xlsx`);
    showToast(`Exported ${filteredClients.length} clients to Excel`, "success");
  };

  const exportToPDF = () => {
    const now = new Date().toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
    setPrintData({
      clients: filteredClients,
      filterLabel: buildFilterLabel(),
      generatedAt: now,
      stats: filteredStats,
    });
    // Small delay so the component mounts before print dialog
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintData(null), 500);
    }, 150);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Clients</h1>
          <p className="text-sm text-stone-400 mt-0.5">Manage client profiles and track payment history</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
          <button
            onClick={exportToPDF}
            disabled={filteredClients.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-white border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors disabled:opacity-40"
            title="Export to PDF"
          >
            <FileDown className="w-4 h-4" />
            PDF
          </button>
          <button
            onClick={exportToExcel}
            disabled={filteredClients.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-white border border-stone-200 text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
            title="Export to Excel"
          >
            <Sheet className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Clients" value={String(stats.total)} icon={Users} color="amber" />
        <StatCard label="With Events" value={String(stats.withEvents)} icon={Calendar} color="violet"
          sub={`${stats.total - stats.withEvents} inactive`} />
        <StatCard label="Total Billed" value={formatCurrency(stats.totalRevenue)} icon={Receipt} color="emerald" />
        <StatCard label="Outstanding Balance" value={formatCurrency(stats.totalBalance)} icon={TrendingUp}
          color={stats.totalBalance > 0 ? "rose" : "emerald"} />
      </div>

      {/* ── Search + Filters ── */}
      <div className="bg-white rounded-2xl border border-stone-200 p-3 mb-4 space-y-3">
        {/* Search row */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name, phone, email or company…"
            className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none bg-stone-50"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-stone-400 hover:text-stone-600"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter pills row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-stone-400" />
            <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide">Balance</span>
          </div>
          <div className="flex gap-1.5">
            <FilterPill label="All" active={balanceFilter === "all"} onClick={() => setBalanceFilter("all")} />
            <FilterPill label="Has Balance" active={balanceFilter === "outstanding"} onClick={() => setBalanceFilter("outstanding")} />
            <FilterPill label="Cleared" active={balanceFilter === "cleared"} onClick={() => setBalanceFilter("cleared")} />
          </div>

          <div className="w-px h-5 bg-stone-200 mx-1 hidden sm:block" />

          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-stone-400" />
            <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide">Events</span>
          </div>
          <div className="flex gap-1.5">
            <FilterPill label="All" active={eventsFilter === "all"} onClick={() => setEventsFilter("all")} />
            <FilterPill label="With Events" active={eventsFilter === "with_events"} onClick={() => setEventsFilter("with_events")} />
            <FilterPill label="No Events" active={eventsFilter === "no_events"} onClick={() => setEventsFilter("no_events")} />
          </div>

          {isFiltered && (
            <button
              onClick={clearAllFilters}
              className="ml-auto flex items-center gap-1 text-xs text-stone-400 hover:text-stone-700 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>

        {/* Active filter summary */}
        {isFiltered && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone-500 pt-0.5">
            <span className="font-semibold text-stone-700">{filteredClients.length}</span> of {clients.length} clients
            {filteredStats.totalRevenue > 0 && (
              <>
                <span className="text-stone-300">·</span>
                <span>Billed: <span className="font-semibold text-stone-700">{formatCurrency(filteredStats.totalRevenue)}</span></span>
              </>
            )}
            {filteredStats.totalBalance > 0 && (
              <>
                <span className="text-stone-300">·</span>
                <span>Balance: <span className="font-semibold text-amber-700">{formatCurrency(filteredStats.totalBalance)}</span></span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <ClientsTable
        clients={filteredClients}
        isFiltered={isFiltered}
        isStaff={isStaff}
        sortCol={sortCol}
        sortDir={sortDir}
        onSort={handleSort}
        onEdit={openEdit}
        onDelete={openDelete}
        onLedger={c => { setLedgerClient(c); setLedgerOpen(true); }}
        onClearFilter={clearAllFilters}
        onAddClient={openAdd}
      />

      {/* ── Modals ── */}
      <Modal isOpen={isFormOpen} onClose={closeForm}
        title={editingClient ? "Edit Client" : "Add New Client"} size="md">
        <ClientForm initialData={editingClient || undefined} onSubmit={handleFormSubmit} onCancel={closeForm} />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeletingClient(null); }}
        onConfirm={handleDelete}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        itemName={deletingClient?.name}
        loading={deleteLoading}
      />

      <ClientLedgerModal
        client={ledgerClient}
        isOpen={ledgerOpen}
        onClose={() => { setLedgerOpen(false); setLedgerClient(null); }}
      />

      {/* ── Printable PDF (hidden, shown only during print) ── */}
      {printData && (
        <PrintableClientsList
          clients={printData.clients}
          filterLabel={printData.filterLabel}
          generatedAt={printData.generatedAt}
          stats={printData.stats}
          businessInfo={{
            name: settings?.business?.businessName || "Shalimar",
            logoUrl: settings?.logoUrl,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
          }}
        />
      )}
    </DashboardLayout>
  );
}
